import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, FolderPlus, Tag,
  Send, CheckCircle, XCircle, Globe, Archive, FileText, Clock, AlertCircle,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth, hasPermission } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface FaqCategory {
  id: string;
  organizationId: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

interface FaqItem {
  id: string;
  organizationId?: string;
  question: string;
  answer?: string;
  category: string;
  faqCategoryId?: string;
  faqCategoryName?: string;
  sortOrder: number;
  createdByUserId?: string;
  updatedAt?: string;
  // Governance
  governanceStatus: string;
  governanceVersion: number;
  submittedByUserId?: string;
  submittedAt?: string;
  reviewedByUserId?: string;
  reviewedAt?: string;
  approvedByUserId?: string;
  approvedAt?: string;
  rejectedByUserId?: string;
  rejectedAt?: string;
  reviewComments?: string;
  publishedAt?: string;
}

// ─── Governance status config (mirrors content-governance.tsx) ─────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  draft:     { label: "Draft",          color: "bg-gray-100 text-gray-700",     icon: <FileText className="h-3 w-3" /> },
  submitted: { label: "Pending Review", color: "bg-yellow-100 text-yellow-800", icon: <Clock className="h-3 w-3" /> },
  approved:  { label: "Approved",       color: "bg-blue-100 text-blue-800",     icon: <CheckCircle className="h-3 w-3" /> },
  rejected:  { label: "Rejected",       color: "bg-red-100 text-red-800",       icon: <XCircle className="h-3 w-3" /> },
  published: { label: "Published",      color: "bg-green-100 text-green-800",   icon: <Globe className="h-3 w-3" /> },
  archived:  { label: "Archived",       color: "bg-slate-100 text-slate-600",   icon: <Archive className="h-3 w-3" /> },
};

function GovernanceBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, color: "bg-gray-100 text-gray-700", icon: null };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.color}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ─── API helpers ──────────────────────────────────────────────────────────────

async function apiGet<T>(url: string): Promise<T> {
  const res = await apiRequest("GET", url);
  return res.json();
}

async function apiPost<T>(url: string, body: unknown): Promise<T> {
  const res = await apiRequest("POST", url, body);
  return res.json();
}

async function apiPut<T>(url: string, body: unknown): Promise<T> {
  const res = await apiRequest("PUT", url, body);
  return res.json();
}

async function apiDelete(url: string): Promise<void> {
  await apiRequest("DELETE", url);
}

// ─── Category Dialog ──────────────────────────────────────────────────────────

function CategoryDialog({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: FaqCategory | null;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(editing?.name ?? "");
  const [err, setErr] = useState("");

  const save = useMutation({
    mutationFn: () =>
      editing
        ? apiPut(`/api/faq-categories/${editing.id}`, { name })
        : apiPost("/api/faq-categories", { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["faq-categories"] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message),
  });

  function handleOpen(val: boolean) {
    if (!val) onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Category" : "New Category"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="cat-name">Name</Label>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => { setName(e.target.value); setErr(""); }}
              placeholder="e.g. Admissions"
              autoFocus
            />
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── FAQ Dialog ───────────────────────────────────────────────────────────────

function FaqDialog({
  open,
  onClose,
  editing,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  editing: FaqItem | null;
  categories: FaqCategory[];
}) {
  const qc = useQueryClient();
  const [question, setQuestion] = useState(editing?.question ?? "");
  const [answer, setAnswer] = useState(editing?.answer ?? "");
  const [faqCategoryId, setFaqCategoryId] = useState<string>(editing?.faqCategoryId ?? "__none__");
  const [sortOrder, setSortOrder] = useState(editing?.sortOrder ?? 0);
  const [err, setErr] = useState("");

  const save = useMutation({
    mutationFn: () => {
      const resolvedCategoryId = faqCategoryId === "__none__" ? null : faqCategoryId || null;
      const payload = {
        question,
        answer: answer || null,
        faqCategoryId: resolvedCategoryId,
        category: categories.find((c) => c.id === resolvedCategoryId)?.name ?? "General",
        sortOrder,
      };
      return editing
        ? apiPut(`/api/faq/${editing.id}`, payload)
        : apiPost("/api/faq", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["faq-items"] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message),
  });

  function handleOpen(val: boolean) {
    if (!val) onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit FAQ" : "New FAQ"}</DialogTitle>
          {editing && editing.governanceStatus !== "draft" && (
            <DialogDescription className="text-amber-600">
              Saving changes will reset this FAQ to Draft status for re-review.
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="faq-question">Question <span aria-hidden="true" className="text-destructive">*</span></Label>
            <Input
              id="faq-question"
              value={question}
              onChange={(e) => { setQuestion(e.target.value); setErr(""); }}
              placeholder="What is the application deadline?"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="faq-answer">Answer</Label>
            <Textarea
              id="faq-answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="The application deadline is…"
              rows={4}
            />
          </div>
          <div>
            <Label htmlFor="faq-category">Category</Label>
            <Select value={faqCategoryId} onValueChange={setFaqCategoryId}>
              <SelectTrigger id="faq-category">
                <SelectValue placeholder="Uncategorised" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Uncategorised</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="faq-sort">Sort Order</Label>
            <Input
              id="faq-sort"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              className="w-28"
            />
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !question.trim()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Reject dialog ────────────────────────────────────────────────────────────

function RejectDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}) {
  const [comment, setComment] = useState("");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setComment(""); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject FAQ</DialogTitle>
          <DialogDescription>
            Provide a mandatory comment explaining why this FAQ is being rejected.
            The creator will see this feedback.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Textarea
            placeholder="Enter rejection reason (required)…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="resize-none"
          />
          {!comment.trim() && (
            <p className="mt-1 text-xs text-red-600">A comment is required to reject an FAQ.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); setComment(""); }}>
            Cancel
          </Button>
          <Button
            onClick={() => { onConfirm(comment.trim()); setComment(""); }}
            disabled={!comment.trim()}
            className="bg-red-600 hover:bg-red-700 text-white">
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function FaqManagementPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  // Permission flags — server enforces the same gates; these drive button visibility only.
  const canCreate         = hasPermission(user, "faq.create");
  const canEdit           = hasPermission(user, "faq.edit");
  const canReview         = hasPermission(user, "content.review");
  const canPublish        = hasPermission(user, "content.publish");
  const canCategoryManage = hasPermission(user, "category.manage");

  // Category state
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<FaqCategory | null>(null);
  const [deleteCatId, setDeleteCatId] = useState<string | null>(null);

  // FAQ state
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
  const [deleteFaqId, setDeleteFaqId] = useState<string | null>(null);

  // Reject dialog
  const [rejectFaqId, setRejectFaqId] = useState<string | null>(null);

  // Filters
  const [searchQ, setSearchQ] = useState("");
  const [filterCatId, setFilterCatId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Data
  const { data: categories = [], isLoading: catsLoading } = useQuery<FaqCategory[]>({
    queryKey: ["faq-categories"],
    queryFn: () => apiGet("/api/faq-categories"),
  });

  const { data: faqs = [], isLoading: faqsLoading } = useQuery<FaqItem[]>({
    queryKey: ["faq-items", filterCatId, searchQ, filterStatus],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterCatId)  params.set("categoryId", filterCatId);
      if (searchQ)      params.set("q", searchQ);
      if (filterStatus) params.set("status", filterStatus);
      return apiGet(`/api/faq?${params.toString()}`);
    },
  });

  // ── Mutations ──

  const deleteCat = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/faq-categories/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["faq-categories"] });
      qc.invalidateQueries({ queryKey: ["faq-items"] });
      setDeleteCatId(null);
    },
  });

  const deleteFaq = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/faq/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["faq-items"] });
      setDeleteFaqId(null);
    },
  });

  const govAction = useMutation({
    mutationFn: ({ id, action, comment }: { id: string; action: string; comment?: string }) => {
      const body = comment ? { comment } : undefined;
      return apiRequest("POST", `/api/faq/${id}/${action}`, body);
    },
    onSuccess: (_data, vars) => {
      const labels: Record<string, string> = {
        submit:  "FAQ submitted for review",
        approve: "FAQ approved",
        reject:  "FAQ rejected",
        publish: "FAQ published",
        archive: "FAQ archived",
      };
      toast({ title: labels[vars.action] ?? "Done" });
      qc.invalidateQueries({ queryKey: ["faq-items"] });
    },
    onError: async (err: unknown) => {
      let message = "Action failed";
      if (err instanceof Response) {
        try { const b = await err.json(); message = b.message ?? message; } catch { /**/ }
      }
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  function openNewCat()  { setEditingCat(null); setCatDialogOpen(true); }
  function openEditCat(c: FaqCategory) { setEditingCat(c); setCatDialogOpen(true); }
  function closeCatDialog() { setCatDialogOpen(false); setEditingCat(null); }

  function openNewFaq()  { setEditingFaq(null); setFaqDialogOpen(true); }
  function openEditFaq(f: FaqItem) { setEditingFaq(f); setFaqDialogOpen(true); }
  function closeFaqDialog() { setFaqDialogOpen(false); setEditingFaq(null); }

  const STATUS_FILTER_OPTIONS = [
    { value: "",          label: "All statuses" },
    { value: "draft",     label: "Draft" },
    { value: "submitted", label: "Pending Review" },
    { value: "approved",  label: "Approved" },
    { value: "rejected",  label: "Rejected" },
    { value: "published", label: "Published" },
  ];

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">FAQ Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage organisation-wide FAQs through the governance lifecycle:
            Draft → Submitted → Approved → Published.
          </p>
        </div>
        {canCreate && (
          <Button onClick={openNewFaq} data-testid="btn-new-faq">
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
            New FAQ
          </Button>
        )}
      </div>

      {/* ── Categories panel ── */}
      <section aria-labelledby="categories-heading">
        <div className="flex items-center justify-between mb-3">
          <h2 id="categories-heading" className="text-base font-medium">Categories</h2>
          {canCategoryManage && (
            <Button variant="outline" size="sm" onClick={openNewCat} data-testid="btn-new-category">
              <FolderPlus className="w-4 h-4 mr-1.5" aria-hidden="true" />
              New Category
            </Button>
          )}
        </div>

        {catsLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : categories.length === 0 ? (
          <p className="text-sm text-muted-foreground">No categories yet. Create one to organise your FAQs.</p>
        ) : (
          <div className="flex flex-wrap gap-2" role="list" aria-label="FAQ categories">
            {categories.map((c) => (
              <div key={c.id} role="listitem"
                className="flex items-center gap-1 bg-muted rounded-md px-2 py-1 text-sm">
                <Tag className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                <span>{c.name}</span>
                <button
                  onClick={() => openEditCat(c)}
                  className="ml-1 text-muted-foreground hover:text-foreground"
                  aria-label={`Edit category ${c.name}`}
                  data-testid={`btn-edit-cat-${c.id}`}>
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setDeleteCatId(c.id)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label={`Delete category ${c.name}`}
                  data-testid={`btn-delete-cat-${c.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── FAQ list ── */}
      <section aria-labelledby="faqs-heading">
        <h2 id="faqs-heading" className="text-base font-medium mb-3">FAQs</h2>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <Input
            placeholder="Search questions or answers…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="max-w-xs"
            aria-label="Search FAQs"
          />
          <Select value={filterCatId || "__none__"} onValueChange={(v) => setFilterCatId(v === "__none__" ? "" : v)}>
            <SelectTrigger className="w-44" aria-label="Filter by category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus || "__all__"} onValueChange={(v) => setFilterStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-44" aria-label="Filter by status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value || "__all__"} value={o.value || "__all__"}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {faqsLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : faqs.length === 0 ? (
          <div className="border rounded-md p-8 text-center text-muted-foreground">
            <p className="text-sm">No FAQs found.</p>
            {canCreate && (
              <Button variant="outline" size="sm" className="mt-3" onClick={openNewFaq}>
                <Plus className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Add your first FAQ
              </Button>
            )}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead className="hidden md:table-cell">Answer</TableHead>
                  <TableHead className="hidden sm:table-cell">Category</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {faqs.map((f) => (
                  <TableRow key={f.id} data-testid={`faq-row-${f.id}`}>
                    <TableCell className="font-medium max-w-xs align-top">
                      <span className="line-clamp-2">{f.question}</span>
                      {/* Rejection feedback shown inline under the question */}
                      {f.governanceStatus === "rejected" && f.reviewComments && (
                        <div className="mt-1.5 flex items-start gap-1 rounded bg-red-50 border border-red-100 px-2 py-1 text-xs text-red-700">
                          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                          <span className="line-clamp-2">{f.reviewComments}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground max-w-xs align-top">
                      <span className="line-clamp-2">{f.answer ?? <em className="text-xs">No answer yet</em>}</span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell align-top">
                      {f.faqCategoryName ? (
                        <Badge variant="secondary">{f.faqCategoryName}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">{f.category}</span>
                      )}
                    </TableCell>

                    {/* Governance status badge */}
                    <TableCell className="align-top">
                      <GovernanceBadge status={f.governanceStatus} />
                      {f.governanceVersion > 0 && (
                        <span className="ml-1 text-xs text-gray-400">v{f.governanceVersion}</span>
                      )}
                    </TableCell>

                    {/* Actions column — role-gated */}
                    <TableCell className="text-right align-top">
                      <div className="flex flex-wrap justify-end gap-1">

                        {/* Edit — faq.edit; only allowed on draft/rejected */}
                        {canEdit && (f.governanceStatus === "draft" || f.governanceStatus === "rejected") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditFaq(f)}
                            aria-label={`Edit FAQ: ${f.question}`}
                            data-testid={`btn-edit-faq-${f.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Submit for review — faq.edit; draft or rejected */}
                        {canEdit && (f.governanceStatus === "draft" || f.governanceStatus === "rejected") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => govAction.mutate({ id: f.id, action: "submit" })}
                            aria-label={`Submit FAQ for review: ${f.question}`}
                            data-testid={`btn-submit-faq-${f.id}`}
                            title="Submit for review">
                            <Send className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}

                        {/* Approve — content.review; submitted */}
                        {canReview && f.governanceStatus === "submitted" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => govAction.mutate({ id: f.id, action: "approve" })}
                            aria-label={`Approve FAQ: ${f.question}`}
                            data-testid={`btn-approve-faq-${f.id}`}
                            title="Approve">
                            <CheckCircle className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}

                        {/* Reject — content.review; submitted or approved */}
                        {canReview && (f.governanceStatus === "submitted" || f.governanceStatus === "approved") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setRejectFaqId(f.id)}
                            aria-label={`Reject FAQ: ${f.question}`}
                            data-testid={`btn-reject-faq-${f.id}`}
                            title="Reject">
                            <XCircle className="w-4 h-4 text-red-600" />
                          </Button>
                        )}

                        {/* Publish — content.publish; approved */}
                        {canPublish && f.governanceStatus === "approved" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => govAction.mutate({ id: f.id, action: "publish" })}
                            aria-label={`Publish FAQ: ${f.question}`}
                            data-testid={`btn-publish-faq-${f.id}`}
                            title="Publish">
                            <Globe className="w-4 h-4 text-green-600" />
                          </Button>
                        )}

                        {/* Delete (archive) — faq.delete */}
                        {hasPermission(user, "faq.delete") && f.governanceStatus !== "archived" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteFaqId(f.id)}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Delete FAQ: ${f.question}`}
                            data-testid={`btn-delete-faq-${f.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* ── Dialogs ── */}
      <CategoryDialog
        open={catDialogOpen}
        onClose={closeCatDialog}
        editing={editingCat}
      />
      <FaqDialog
        open={faqDialogOpen}
        onClose={closeFaqDialog}
        editing={editingFaq}
        categories={categories}
      />

      {/* Reject dialog */}
      <RejectDialog
        open={!!rejectFaqId}
        onClose={() => setRejectFaqId(null)}
        onConfirm={(comment) => {
          if (rejectFaqId) {
            govAction.mutate({ id: rejectFaqId, action: "reject", comment });
            setRejectFaqId(null);
          }
        }}
      />

      {/* Delete category confirm */}
      <AlertDialog open={!!deleteCatId} onOpenChange={(v) => { if (!v) setDeleteCatId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>
              FAQs assigned to this category will become uncategorised. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCatId && deleteCat.mutate(deleteCatId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete FAQ confirm */}
      <AlertDialog open={!!deleteFaqId} onOpenChange={(v) => { if (!v) setDeleteFaqId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ?</AlertDialogTitle>
            <AlertDialogDescription>
              This FAQ will be archived and removed from the list. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteFaqId && deleteFaq.mutate(deleteFaqId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
